import type { AnnotationSurfaceKind } from "./annotationSurface.types";

export const ANNOTATION_VOICE_COMMANDS = {
  start: ["start annotation", "dictate note", "capture note"],
  markerOnly: ["drop marker: check continuity here", "mark this frame: pacing issue"],
  structured: [
    "add continuity note: prop changes hands here",
    "add creative note: hold this shot longer",
    "add audio note: linked cue lands late",
    "add npc note: trigger crowd reaction on this beat",
    "add timing note: cut two frames later",
    "add intent note: this should feel unresolved",
  ],
  action: ["action: tighten this beat", "next action: confirm eyeline match", "set action: lift the music cue"],
  commit: ["publish annotation", "publish and close annotation", "complete annotation", "save marker", "close annotation"],
} as const;

export type ParsedAnnotationVoiceIntent = {
  kind: AnnotationSurfaceKind | "marker";
  rawText: string;
  source: "voice";
};

export type ParsedAnnotationVoiceInput = ParsedAnnotationVoiceIntent & {
  command: "draft" | "marker" | "publish" | "publish_close" | "close" | "start" | "action";
  cleanedText: string;
};

function trimCommandPrefix(input: string): string {
  const value = input.trim();
  const separators = [":", "-"];
  for (const separator of separators) {
    const index = value.indexOf(separator);
    if (index >= 0) return value.slice(index + 1).trim();
  }

  return value;
}

function trimActionPrefix(input: string): string {
  const value = trimCommandPrefix(input);
  return value.replace(/^(set\s+action|next\s+action|action)\b[:\-\s]*/i, "").trim() || value;
}

export function inferAnnotationKindFromVoice(input: string): ParsedAnnotationVoiceIntent["kind"] {
  const value = input.trim().toLowerCase();

  if (value.startsWith("drop marker") || value.startsWith("mark this frame")) return "marker";
  if (value.includes("continuity")) return "continuity";
  if (value.includes("creative")) return "creative";
  if (value.includes("timing")) return "timing";
  if (value.includes("audio")) return "audio";
  if (value.includes("npc")) return "npc";
  if (value.includes("intent")) return "intent";
  if (value.includes("publish")) return "publish";
  return "creative";
}

export function parseAnnotationVoiceInput(input: string): ParsedAnnotationVoiceInput {
  const rawText = input.trim();
  const value = rawText.toLowerCase();

  if (ANNOTATION_VOICE_COMMANDS.commit.some((command) => value === command)) {
    if (value === "publish and close annotation" || value === "complete annotation") {
      return { kind: "publish", rawText, cleanedText: "", command: "publish_close", source: "voice" };
    }
    if (value === "save marker") {
      return { kind: "marker", rawText, cleanedText: "", command: "marker", source: "voice" };
    }
    if (value === "close annotation") {
      return { kind: "creative", rawText, cleanedText: "", command: "close", source: "voice" };
    }
    return { kind: "publish", rawText, cleanedText: "", command: "publish", source: "voice" };
  }

  if (ANNOTATION_VOICE_COMMANDS.start.some((command) => value === command)) {
    return { kind: "creative", rawText, cleanedText: "", command: "start", source: "voice" };
  }

  if (value.startsWith("action") || value.startsWith("next action") || value.startsWith("set action")) {
    return {
      kind: "creative",
      rawText,
      cleanedText: trimActionPrefix(rawText),
      command: "action",
      source: "voice",
    };
  }

  if (value.startsWith("drop marker") || value.startsWith("mark this frame")) {
    const cleanedText = trimCommandPrefix(rawText);
    const inferredKind = inferAnnotationKindFromVoice(cleanedText);
    return {
      kind: inferredKind === "marker" ? "creative" : inferredKind,
      rawText,
      cleanedText,
      command: "marker",
      source: "voice",
    };
  }

  const kind = inferAnnotationKindFromVoice(rawText);
  return {
    kind: kind === "marker" ? "creative" : kind,
    rawText,
    cleanedText: trimCommandPrefix(rawText),
    command: "draft",
    source: "voice",
  };
}